import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  TemplateRef,
  ViewEncapsulation,
} from '@angular/core';
import {
  PhxTableCellStringInput,
  PhxTableCellStringSegment,
  PhxTableCellTagPlacement,
} from '../../phx-table.types';
import { PhxTextDecoration, PhxTextIntent } from '../../../text/phx-text.types';
import { PhxTagContrast, PhxTagSize, PhxTagType } from '../../../tag/phx-tag.types';
import { Bullet16 } from '../../../icon/icon-map';
import { normalizeCellStringInput, trackBySegmentValue } from '../phx-table-cell.utils';
import { PHX_TABLE_CELL_TAG_PLACEMENT, TAG_SIZE_BY_PLACEMENT } from '../phx-table-cell.const';

/** A normalized text segment: value, tooltip content, and resolved style knobs. */
interface ResolvedTextSegment {
  value: string;
  tooltipContent: string | TemplateRef<any> | null;
  intent: PhxTextIntent | undefined;
  emphasis: boolean;
  italic: boolean;
  decoration: PhxTextDecoration | undefined;
  truncate: boolean;
}

@Component({
  selector: 'phx-table-cell-tag',
  templateUrl: './phx-table-cell-tag.component.html',
  styleUrls: ['./phx-table-cell-tag.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'phx-table-cell-tag',
    '[class.phx-table-cell-tag-tag-only]': `phxTagPlacement === PHX_TABLE_CELL_TAG_PLACEMENT.TAG_ONLY`,
    '[class.phx-table-cell-tag-as-primary]': `phxTagPlacement === PHX_TABLE_CELL_TAG_PLACEMENT.AS_PRIMARY`,
    '[class.phx-table-cell-tag-above-primary]': `phxTagPlacement === PHX_TABLE_CELL_TAG_PLACEMENT.ABOVE_PRIMARY`,
    '[class.phx-table-cell-tag-below-primary]': `phxTagPlacement === PHX_TABLE_CELL_TAG_PLACEMENT.BELOW_PRIMARY`,
    '[class.phx-table-cell-tag-right-of-primary]': `phxTagPlacement === PHX_TABLE_CELL_TAG_PLACEMENT.RIGHT_OF_PRIMARY`,
    '[class.phx-table-cell-tag-right-of-secondary]': `phxTagPlacement === PHX_TABLE_CELL_TAG_PLACEMENT.RIGHT_OF_SECONDARY`,
  },
})
export class PhxTableCellTagComponent implements OnChanges {
  /** Primary line (medium / standard). Omit for tag-only / as-primary placements. */
  @Input() phxPrimaryText: string | PhxTableCellStringSegment | undefined;
  /** Secondary subtext (small / subdued). Accepts a string, a styled segment, or an array of either (bullet-separated), matching phx-table-cell-string. */
  @Input() phxSecondaryText: PhxTableCellStringInput | undefined;
  /** Render the primary line as an in-app link (`routerLink`). */
  @Input() phxPrimaryLink: string | unknown[] | undefined;
  /** Tag text. Required. */
  @Input() phxTagLabel!: string;
  /** Tag color intent. */
  @Input() phxTagType: PhxTagType = 'neutral';
  /** Tag contrast. */
  @Input() phxTagContrast: PhxTagContrast = 'low';
  /** Tooltip shown on hover over the tag. */
  @Input() phxTagTooltip: string | TemplateRef<any> | undefined;
  /** Where the tag renders relative to the text (also determines tag size). */
  @Input() phxTagPlacement: PhxTableCellTagPlacement = PHX_TABLE_CELL_TAG_PLACEMENT.TAG_ONLY;

  public readonly Bullet16Icon = Bullet16;
  public readonly trackBySegment = trackBySegmentValue;
  public secondaryValue: PhxTableCellStringSegment[] | undefined;
  public readonly PHX_TABLE_CELL_TAG_PLACEMENT = PHX_TABLE_CELL_TAG_PLACEMENT;

  public ngOnChanges(changes: SimpleChanges): void {
    const { phxSecondaryText } = changes;

    if (phxSecondaryText) {
      this.secondaryValue = this.phxSecondaryText ? normalizeCellStringInput(this.phxSecondaryText) : undefined;
    }
  }

  public get primary(): ResolvedTextSegment | null {
    return this.normalizeSegment(this.phxPrimaryText);
  }

  public get resolvedTagSize(): PhxTagSize {
    return TAG_SIZE_BY_PLACEMENT[this.phxTagPlacement] ?? 'small';
  }

  private normalizeSegment(input: string | PhxTableCellStringSegment | undefined): ResolvedTextSegment | null {
    if (input === undefined) {
      return null;
    }
    if (typeof input === 'string') {
      return {
        value: input,
        tooltipContent: null,
        intent: undefined,
        emphasis: false,
        italic: false,
        decoration: undefined,
        truncate: false,
      };
    }
    return {
      value: input.value,
      tooltipContent: input.tooltipContent ?? null,
      intent: input.intent,
      emphasis: input.emphasis ?? false,
      italic: input.italic ?? false,
      decoration: input.decoration,
      truncate: input.truncate ?? false,
    };
  }
}
